import { safeFetchAPI } from '@/api';
import { utils } from '@/helpers';
import { PaginatedData, Pagination, SponsoredCoMarketingTemplate, SponsoredCoMarketingTemplateWithCampaign } from '@/types';
import { create } from 'zustand';
import { useCoMarketingStore } from './useCoMarketingStore';

type Paginator = Pagination | ((pagination: Pagination) => Pagination);

export type SponsoringTemplatesStoreState = {
	loaded: boolean;
	loading: boolean;
	templates: SponsoredCoMarketingTemplateWithCampaign[];

	confirmingStatus: boolean;
	confirmingPublishID: number | undefined;
} & Pagination;

export type SponsoringTemplatesStoreActions = {
	fetchTemplates: (pagination?: Paginator) => Promise<void>;
	installTemplate: (template: SponsoredCoMarketingTemplate) => Promise<void>;
	createTemplate: (template: SponsoredCoMarketingTemplate) => Promise<void>;
	updateTemplate: (template: SponsoredCoMarketingTemplate) => Promise<void>;

	setTemplatePublishStatus: (status: boolean, id?: number) => Promise<SponsoredCoMarketingTemplate | undefined>;
	clearConfirmingPublishStatus: () => void;

	nextPage: () => void;
	previousPage: () => void;
};

export type SponsoringTemplatesStore = SponsoringTemplatesStoreState & SponsoringTemplatesStoreActions;

const initialState: SponsoringTemplatesStoreState = {
	loaded: false,
	loading: false,
	templates: [],
	page: 1,
	limit: 10,
	totalResult: 0,
	confirmingPublishID: undefined,
	confirmingStatus: false,
};

export const useSponsoringTemplatesStore = create<SponsoringTemplatesStore>((set, get) => ({
	...initialState,
	fetchTemplates: async (pagination?: Paginator) => {
		set({ loading: true });
		const current = { page: get().page, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;
		const [templates, error] = await safeFetchAPI<PaginatedData<SponsoredCoMarketingTemplateWithCampaign>>('/co-marketing/sponsoring/:uid', {
			queryParams: {
				includeCampaign: true,
				page: paginator?.page || 1,
				limit: paginator?.limit || 10,
			},
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsoring templates',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}
		set({ ...paginator, templates: templates?.data ?? get().templates, loaded: true, loading: false });
	},
	installTemplate: async (template: SponsoredCoMarketingTemplate) => {
		// Implementation needed
	},
	createTemplate: async (template: SponsoredCoMarketingTemplate) => {
		// Implementation needed
	},
	updateTemplate: async (template: SponsoredCoMarketingTemplate) => {
		// Implementation needed
	},

	nextPage: () => {
		const totalPages = Math.ceil((get().totalResult ?? 0) / (get().limit ?? 0));
		if (get().page < totalPages) {
			get().fetchTemplates({ page: get().page + 1, limit: get().limit });
		}
	},

	previousPage: () => {
		if (get().page > 1) {
			get().fetchTemplates({ page: get().page - 1, limit: get().limit });
		}
	},

	setTemplatePublishStatus: async (status: boolean, id?: number) => {
		const template = get().templates.find((t) => t.id === id);
		if (!template) {
			utils?.toaster?.addToast?.({
				message: 'Template not found',
				type: 'danger',
			});
			return;
		}

		if (!get().confirmingPublishID) {
			set({ confirmingPublishID: id, confirmingStatus: status });
			return;
		}

		const updated = await useCoMarketingStore.getState().setCoMarketingTemplatePublishStatus(status, id);

		if (!updated) {
			return;
		}

		// Update the template in the store
		set({ templates: get().templates.map((t) => (t.id === id ? { ...t, published: status } : t)) });

		set({ confirmingPublishID: undefined });

		return template;
	},

	clearConfirmingPublishStatus: () => {
		set({ confirmingPublishID: undefined, confirmingStatus: false });
	},
}));
